import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { addCsrfHeader } from '../utils/csrfUtils';
import { getTodayLocalDateString, getDateWithOffset } from '../utils/dateUtils';


export const useDeletePantryIngredient = () => {
    const [isDeleting, setIsDeleting] = useState(false);
    const [error, setError] = useState(null);
    const { sessionToken } = useAuth();

    const deletePantryIngredient = async (id, pantryId, appUserId) => {
        setIsDeleting(true);
        setError(null);
        
        try {
            if (!sessionToken) {
                throw new Error('No valid session found');
            }

            // Add CSRF token and auth header
            const headers = await addCsrfHeader({
                'Authorization': `Bearer ${sessionToken}`
            });

            const response = await fetch(`/api/v1/pantry-ingredient/${id}/pantry/${pantryId}/app_user/${appUserId}`, {
                method: 'DELETE',
                headers
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to delete pantry ingredient');
            }

            return true;
        } catch (err) {
            setError(err.message);
            return false;
        } finally {
            setIsDeleting(false);
        }
    };

    return {
        deletePantryIngredient,
        isDeleting,
        error
    };
};

export const useUpdatePantryIngredient = () => {
    const [isUpdating, setIsUpdating] = useState(false);
    const [error, setError] = useState(null);
    const { sessionToken } = useAuth();

    const updatePantryIngredient = async (id, pantryId, appUserId, updates) => {
        setIsUpdating(true);
        setError(null);

        try {
            if (!sessionToken) {
                throw new Error('No valid session found');
            }

            // Add CSRF token and auth header
            const headers = await addCsrfHeader({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            });

            const response = await fetch(`/api/v1/pantry-ingredient/${id}/pantry/${pantryId}/app_user/${appUserId}`, {
                method: 'PUT',
                headers,
                body: JSON.stringify(updates),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to update pantry ingredient');
            }

            const data = await response.json();
            return data;
        } catch (err) {
            setError(err.message);
            return false;
        } finally {
            setIsUpdating(false);
        }
    };

    const updatePantryIngredientConfirmedStatusByIngredientIdAndPantryIdAndAppUserId = async (ingredientId, pantryId, appUserId, isConfirmed, confirmedAt) => {
        setIsUpdating(true);
        setError(null);

        try {
            if (!sessionToken) {
                throw new Error('No valid session found');
            }

            // Add CSRF token and auth header
            const headers = await addCsrfHeader({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            });

            const response = await fetch(`/api/v1/pantry-ingredient/ingredient_id/${ingredientId}/pantry_id/${pantryId}/app_user/${appUserId}`, {
                method: 'PUT',
                headers,
                body: JSON.stringify({ is_confirmed: isConfirmed, confirmed_at: confirmedAt }),
            });

            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.error || 'Failed to update pantry ingredient confirmed status');
            }

            return { ok: response.ok, ...data };
        } catch (err) {
            console.error('Error in updatePantryIngredientConfirmedStatus:', err);
            setError(err.message);
            return { ok: false, error: err.message };
        }
    };

    const pantryIngredientNoLongerInPantryByIngredientIdAndPantryIdAndAppUserId = async (ingredientId, pantryId, appUserId, groceryListId) => {
        setIsUpdating(true);
        setError(null);

        console.log("pantryIngredientNoLongerInPantryByIngredientIdAndPantryIdAndAppUserId: ", ingredientId, " pantry id: ", pantryId, " app user id: ", appUserId, " grocery list id: ", groceryListId);
        
        try {
            if (!sessionToken) {
                throw new Error('No valid session found');
            }

            // Add CSRF token and auth header
            const headers = await addCsrfHeader({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            });

            // First update the pantry ingredient
            const response = await fetch(`/api/v1/pantry-ingredient/ingredient_no_longer_in_pantry/${ingredientId}/${pantryId}/${appUserId}`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ quantity: 0 }),
            });

            const data = await response.json();

            if (!response.ok) {
                console.error('Server error:', data);
                throw new Error(data.error || 'Failed to update pantry ingredient quantity');
            }

            // Now refresh the grocery list ingredient quantities
            const refreshResponse = await fetch(`/api/v1/grocery-list-ingredient/refresh_grocery_list_ingredient_quantity/${ingredientId}/grocery_list/${groceryListId}/app_user/${appUserId}`, {
                method: 'POST',
                headers
            });

            if (!refreshResponse.ok) {
                console.warn('Failed to refresh grocery list quantities:', await refreshResponse.json());
            } else {
                console.log('Grocery list ingredient refreshed successfully');
            }

            return { ok: true, data };
            
        } catch (err) {
            console.error('Error in pantryIngredientNoLongerInPantry:', err);
            setError(err.message);
            return { ok: false, error: err.message };
        } finally {
            setIsUpdating(false);
        }
    };

    return {
        updatePantryIngredient,
        updatePantryIngredientConfirmedStatusByIngredientIdAndPantryIdAndAppUserId,
        pantryIngredientNoLongerInPantryByIngredientIdAndPantryIdAndAppUserId,
        isUpdating,
        error
    };
};

export const useAddPantryIngredient = () => {
    const [isAdding, setIsAdding] = useState(false);
    const [error, setError] = useState(null);
    const { sessionToken } = useAuth();

    const addPantryIngredient = async (pantryIngredientData) => {
        setIsAdding(true);
        setError(null);

        try {
            if (!sessionToken) {
                throw new Error('No valid session found');
            }

            // Add CSRF token and auth header
            const headers = await addCsrfHeader({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            });

            const response = await fetch('/api/v1/pantry-ingredient', {
                method: 'POST',
                headers,
                body: JSON.stringify(pantryIngredientData),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to add pantry ingredient');
            }

            const data = await response.json();
            return data;
        } catch (err) {
            setError(err.message);
            return false;
        } finally {
            setIsAdding(false);
        }
    };

    return {
        addPantryIngredient,
        isAdding,
        error
    };
};

export const useAddPantryIngredients = () => {
    const [isAdding, setIsAdding] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [error, setError] = useState(null);
    const { sessionToken } = useAuth();

    const addPantryIngredients = async (pantryIngredientsData, isConfirmed = null) => {
        setIsAdding(true);
        setError(null);

        try {
            if (!sessionToken) {
                throw new Error('No valid session found');
            }

            // Add CSRF token and auth header
            const headers = await addCsrfHeader({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            });

            // If isConfirmed is provided, add it to each ingredient
            const ingredientsWithConfirmation = isConfirmed !== null
                ? pantryIngredientsData.map(ingredient => ({
                    ...ingredient,
                    is_confirmed: isConfirmed,
                    // If confirming, also set the confirmed_at timestamp
                    ...(isConfirmed === true && { confirmed_at: new Date().toISOString() })
                }))
                : pantryIngredientsData;

            const response = await fetch('/api/v1/pantry-ingredient/multiple', {
                method: 'POST',
                headers,
                body: JSON.stringify({ ingredients: ingredientsWithConfirmation }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to add pantry ingredients');
            }

            const data = await response.json();
            return data;
        } catch (err) {
            setError(err.message);
            return false;
        } finally {
            setIsAdding(false);
        }
    };

    const deleteMultiplePantryIngredients = async (pantryIngredientIds, appUserId) => {
        setIsDeleting(true);
        setError(null);

        try {
            if (!sessionToken) {
                throw new Error('No valid session found');
            }

            // Add CSRF token and auth header
            const headers = await addCsrfHeader({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            });

            const response = await fetch(`/api/v1/pantry-ingredient/multiple/app_user/${appUserId}`, {
                method: 'DELETE',
                headers,
                body: JSON.stringify({ ids: pantryIngredientIds }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to delete pantry ingredients');
            }

            return true;
        } catch (err) {
            setError(err.message);
            return false;
        } finally {
            setIsDeleting(false);
        }
    };

    return {
        addPantryIngredients,
        deleteMultiplePantryIngredients,
        isAdding,
        isDeleting,
        error
    };
};

// Hook for when user confirms they have enough of an ingredient
export const useConfirmHasEnough = () => {
    const [isUpdating, setIsUpdating] = useState(false);
    const [error, setError] = useState(null);
    const { sessionToken } = useAuth();

    const confirmHasEnough = async (ingredientId, pantryId, appUserId, quantity, unitOfMeasurement) => {
        setIsUpdating(true);
        setError(null);

        try {
            if (!sessionToken) {
                throw new Error('No valid session found');
            }

            // Add CSRF token and auth header
            const headers = await addCsrfHeader({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            });

            // Step 1: Confirm the pantry ingredient quantity
            const response = await fetch(`/api/v1/pantry-ingredient/ingredient_id/${ingredientId}/pantry_id/${pantryId}/app_user/${appUserId}/has_enough`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    ingredient_id: ingredientId,
                    pantry_id: pantryId,
                    quantity: quantity,
                    unit_of_measurement: unitOfMeasurement
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to confirm pantry ingredient quantity');
            }

            const data = await response.json();

            // Step 2: Update relevant grocery list ingredients
            const todaysBrowserDate = getTodayLocalDateString(); //YYYY-MM-DD
            const nextWeekBrowserDate = getDateWithOffset(7); //YYYY-MM-DD

            const groceryListResponse = await fetch('/api/v1/pantry-ingredient/refresh_grocery_list_ingredients_on_pantry_ingredient_crud', {
                method: 'POST',
                headers,
                body: JSON.stringify({ 
                    ingredientId, 
                    appUserId, 
                    todaysBrowserDate, 
                    nextWeekBrowserDate 
                })
            });

            if (!groceryListResponse.ok) {
                console.warn('Failed to refresh grocery list ingredients, but pantry update succeeded');
            }

            return data;
        } catch (err) {
            console.error('Error in confirmHasEnough:', err);
            setError(err.message);
            throw err;
        } finally {
            setIsUpdating(false);
        }
    };

    return {
        confirmHasEnough,
        isUpdating,
        error
    };
};

// Hook for when user confirms they have less than enough (or none) of an ingredient
export const useConfirmHasLessThanEnough = () => {
    const [isUpdating, setIsUpdating] = useState(false);
    const [error, setError] = useState(null);
    const { sessionToken } = useAuth();

    const confirmHasLessThanEnough = async (ingredientId, pantryId, appUserId, quantity, unitOfMeasurement) => {
        setIsUpdating(true);
        setError(null);

        try {
            if (!sessionToken) {
                throw new Error('No valid session found');
            }

            // Add CSRF token and auth header
            const headers = await addCsrfHeader({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            });

            // Step 1: Confirm the pantry ingredient quantity
            const response = await fetch(`/api/v1/pantry-ingredient/ingredient_id/${ingredientId}/pantry_id/${pantryId}/app_user/${appUserId}/has_less_than_enough`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    ingredient_id: ingredientId,
                    pantry_id: pantryId,
                    quantity: quantity,
                    unit_of_measurement: unitOfMeasurement
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to confirm pantry ingredient quantity');
            }

            const data = await response.json();

            // Step 2: Update relevant grocery list ingredients
            const todaysBrowserDate = getTodayLocalDateString(); //YYYY-MM-DD
            const nextWeekBrowserDate = getDateWithOffset(7); //YYYY-MM-DD

            const groceryListResponse = await fetch('/api/v1/pantry-ingredient/refresh_grocery_list_ingredients_on_pantry_ingredient_crud', {
                method: 'POST',
                headers,
                body: JSON.stringify({ 
                    ingredientId, 
                    appUserId, 
                    todaysBrowserDate, 
                    nextWeekBrowserDate 
                })
            });

            if (!groceryListResponse.ok) {
                console.warn('Failed to refresh grocery list ingredients, but pantry update succeeded');
            }

            return data;
        } catch (err) {
            console.error('Error in confirmHasLessThanEnough:', err);
            setError(err.message);
            throw err;
        } finally {
            setIsUpdating(false);
        }
    };

    return {
        confirmHasLessThanEnough,
        isUpdating,
        error
    };
};